import { useEffect, useState, useCallback, useRef } from "react";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { FloppyDiskIcon, GearSixIcon, PlusIcon, TrashIcon, CopyIcon, DownloadSimpleIcon, KeyIcon } from "@phosphor-icons/react";
import { Field, TextInput } from "@/components/CarFormFields";

export default function Settings() {
  const [form, setForm] = useState({
    description_signature: "",
    description_signature_en: "",
    company_phone: "",
    company_email: "",
    company_website: "",
    wp_site_url: "",
    wp_webhook_token: "",
    storage_quota_mb: 5120,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tokens, setTokens] = useState([]);
  const [newTokenName, setNewTokenName] = useState("");
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState(null); // plain token shown ONCE after creation
  const [pluginVersion, setPluginVersion] = useState("");

  const loadTokens = useCallback(async () => {
    try {
      const r = await api.get("/wp-sync/tokens");
      setTokens(r.data);
    } catch {
      // not admin or feature unavailable — silently ignore
    }
  }, []);

  useEffect(() => {
    api.get("/settings")
      .then((r) => setForm((f) => ({ ...f, ...r.data })))
      .catch(() => toast.error("Αποτυχία φόρτωσης ρυθμίσεων"))
      .finally(() => setLoading(false));
    loadTokens();
    api.get("/wp-sync/plugin-version")
      .then((r) => setPluginVersion(r.data?.version || ""))
      .catch(() => {});
  }, [loadTokens]);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/settings", form);
      setForm({ ...form, ...r.data });
      toast.success("Οι ρυθμίσεις αποθηκεύτηκαν");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="p-12 text-center text-[#4B5563]">Φόρτωση...</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <GearSixIcon size={24} weight="duotone" className="text-[#0A0A0A]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">ΕΝΟΤΗΤΑ</div>
            <h1 className="heading text-3xl font-extrabold">Ρυθμίσεις</h1>
          </div>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-5 py-2.5 flex items-center gap-2 text-sm font-medium"
          data-testid="settings-save-btn"
        >
          <FloppyDiskIcon size={16} weight="bold" />
          {saving ? "Αποθήκευση..." : "Αποθήκευση"}
        </button>
      </div>

      {/* Signatures section */}
      <section className="border border-black/10 bg-white">
        <div className="px-5 py-3 border-b border-black/10 bg-[#F9FAFB] flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">01</div>
            <h2 className="heading font-bold text-base">Υπογραφή Περιγραφής</h2>
          </div>
          <div className="text-xs text-[#4B5563] max-w-md text-right">
            Προστίθεται αυτόματα στο τέλος της περιγραφής κάθε οχήματος <br/>(σε car.gr, WordPress/Polylang exports & PDF brochure).
          </div>
        </div>
        <div className="p-5 space-y-5">
          <Field label="Υπογραφή Ελληνικών (ισχύει για όλα τα οχήματα)">
            <textarea
              value={form.description_signature}
              onChange={(e) => set("description_signature", e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
              placeholder="--&#10;BESIKOS CARS · Μεταχειρισμένα Αυτοκίνητα&#10;Διεύθυνση · Τηλέφωνο · email · website"
              data-testid="signature-el-textarea"
            />
            <div className="text-[10px] text-[#4B5563] mt-1 font-mono">
              {form.description_signature.length} χαρακτήρες · θα τυπώνεται μετά από κενή γραμμή στο τέλος της περιγραφής
            </div>
          </Field>

          <Field label="Υπογραφή Αγγλικών (για Polylang EN posts)">
            <textarea
              value={form.description_signature_en}
              onChange={(e) => set("description_signature_en", e.target.value)}
              rows={6}
              className="w-full px-3 py-2 border border-black/20 bg-white outline-none focus:border-[#0055FF] text-sm"
              placeholder="--&#10;BESIKOS CARS · Used Cars&#10;Address · Phone · email · website"
              data-testid="signature-en-textarea"
            />
            <div className="text-[10px] text-[#4B5563] mt-1 font-mono">
              {form.description_signature_en.length} χαρακτήρες
            </div>
          </Field>
        </div>
      </section>

      {/* Company info section */}
      <section className="border border-black/10 bg-white">
        <div className="px-5 py-3 border-b border-black/10 bg-[#F9FAFB]">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">02</div>
          <h2 className="heading font-bold text-base">Στοιχεία Επικοινωνίας</h2>
        </div>
        <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Τηλέφωνο">
            <TextInput
              value={form.company_phone}
              onChange={(e) => set("company_phone", e.target.value)}
              placeholder="2310-000000"
              data-testid="company-phone-input"
            />
          </Field>
          <Field label="Email">
            <TextInput
              value={form.company_email}
              onChange={(e) => set("company_email", e.target.value)}
              placeholder="info@besikos.gr"
              data-testid="company-email-input"
            />
          </Field>
          <Field label="Website">
            <TextInput
              value={form.company_website}
              onChange={(e) => set("company_website", e.target.value)}
              placeholder="https://besikos.gr"
              data-testid="company-website-input"
            />
          </Field>
        </div>
      </section>

      {/* WP Webhook — instant sync trigger */}
      <section className="bg-white border border-black/10 p-6" data-testid="wp-webhook-section">
        <h3 className="heading text-lg font-bold mb-1">⚡ WordPress Webhook (άμεσος συγχρονισμός)</h3>
        <p className="text-sm text-[#4B5563] mb-4">
          Όταν τσεκάρετε «Site» σε ένα αυτοκίνητο, το BESIKOS στέλνει άμεσα ένα ping στο WordPress σας για να γίνει sync μέσα σε δευτερόλεπτα.
          Συμπληρώστε τα παρακάτω αφού ανοίξετε <strong>BESIKOS Sync → Webhook Token</strong> στο WordPress admin.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="WordPress Site URL">
            <TextInput
              value={form.wp_site_url}
              onChange={(e) => set("wp_site_url", e.target.value)}
              placeholder="https://besikoscars.gr"
              data-testid="wp-site-url-input"
            />
          </Field>
          <Field label="Webhook Token">
            <TextInput
              value={form.wp_webhook_token}
              onChange={(e) => set("wp_webhook_token", e.target.value)}
              placeholder="(αντιγραφή από WP admin)"
              data-testid="wp-webhook-token-input"
            />
          </Field>
        </div>
      </section>

      {/* WordPress sync API tokens */}
      <section className="bg-white border border-black/10 p-6">
        <h3 className="heading text-lg font-bold mb-1 flex items-center gap-2">
          <KeyIcon size={20} /> API Tokens για WordPress Sync
        </h3>
        <p className="text-sm text-[#4B5563] mb-4">
          Δημιουργήστε ένα token για το BESIKOS CARS WordPress plugin. Κάθε token δίνει <strong>read-only</strong> πρόσβαση στα αυτοκίνητα.
        </p>

        <div className="bg-[#F9FAFB] border border-black/10 p-4 mb-4">
          <div className="text-xs uppercase tracking-wider text-[#6B7280] mb-2">WordPress Plugin</div>
          <div className="flex items-center gap-3 flex-wrap">
            <a
              href={`${API}/wp-sync/plugin.zip${pluginVersion ? `?v=${encodeURIComponent(pluginVersion)}` : ""}`}
              className="inline-flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 text-sm font-medium hover:bg-[#1F2937]"
              data-testid="download-wp-plugin"
            >
              <DownloadSimpleIcon size={16} /> Λήψη besikos-cars-sync.zip
            </a>
            {pluginVersion && (
              <span className="inline-flex items-center px-2.5 py-1 bg-[#0055FF] text-white text-xs font-mono font-medium" data-testid="wp-plugin-version">
                v{pluginVersion}
              </span>
            )}
          </div>
          <p className="text-xs text-[#4B5563] mt-2">
            Κατεβάστε το .zip και ανεβάστε το στο WP admin → Plugins → Add New → Upload Plugin.
          </p>
        </div>

        {/* New token form */}
        <div className="flex gap-2 mb-4">
          <input
            type="text"
            value={newTokenName}
            onChange={(e) => setNewTokenName(e.target.value)}
            placeholder='Όνομα token (πχ "WP besikos.akalemi.gr")'
            className="flex-1 px-3 py-2 border border-black/20 text-sm outline-none focus:border-[#0055FF]"
            data-testid="new-token-name"
          />
          <button
            onClick={async () => {
              if (!newTokenName.trim()) { toast.error("Δώστε όνομα"); return; }
              setCreating(true);
              try {
                const r = await api.post("/wp-sync/tokens", { name: newTokenName.trim() });
                setRevealed(r.data);
                setNewTokenName("");
                await loadTokens();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Αποτυχία δημιουργίας");
              } finally { setCreating(false); }
            }}
            disabled={creating}
            className="bg-[#374151] text-white px-4 py-2 text-sm font-medium hover:bg-[#1F2937] disabled:opacity-50 flex items-center gap-1.5"
            data-testid="create-token-btn"
          >
            <PlusIcon size={16} /> Δημιουργία
          </button>
        </div>

        {/* Reveal newly-created plain token */}
        {revealed && (
          <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-4 mb-4">
            <div className="text-xs uppercase tracking-wider text-[#92400E] font-bold mb-2">
              ⚠️ Αντιγράψτε ΤΩΡΑ — δε θα εμφανιστεί ξανά
            </div>
            <div className="flex items-center gap-2 bg-white border border-[#F59E0B] p-2">
              <code className="flex-1 text-xs break-all font-mono">{revealed.plain_token}</code>
              <button
                onClick={() => { navigator.clipboard.writeText(revealed.plain_token); toast.success("Αντιγράφηκε"); }}
                className="bg-[#374151] text-white p-1.5 hover:bg-[#1F2937]"
              ><CopyIcon size={14} /></button>
            </div>
            <button
              onClick={() => setRevealed(null)}
              className="mt-2 text-xs text-[#92400E] underline hover:text-[#7C2D12]"
            >Έκλεισα και αντέγραψα — απόκρυψη</button>
          </div>
        )}

        {/* Token list */}
        {tokens.length === 0 ? (
          <p className="text-sm text-[#6B7280] italic">Δεν υπάρχουν tokens ακόμα.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F9FAFB]">
              <tr className="text-left text-xs uppercase tracking-wider text-[#6B7280]">
                <th className="px-3 py-2">Όνομα</th>
                <th className="px-3 py-2">Prefix</th>
                <th className="px-3 py-2">Χρήσεις</th>
                <th className="px-3 py-2">Τελευταία χρήση</th>
                <th className="px-3 py-2">Κατάσταση</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {tokens.map((t) => (
                <tr key={t.id} className="border-b border-black/5">
                  <td className="px-3 py-2 font-medium">{t.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-[#6B7280]">{t.prefix}</td>
                  <td className="px-3 py-2 font-mono text-xs">{t.use_count || 0}</td>
                  <td className="px-3 py-2 text-xs text-[#6B7280]">{t.last_used_at ? new Date(t.last_used_at).toLocaleString("el-GR") : "—"}</td>
                  <td className="px-3 py-2">
                    {t.revoked
                      ? <span className="bg-[#FEE2E2] text-[#991B1B] text-[10px] px-2 py-0.5 uppercase tracking-wider">ανακλήθηκε</span>
                      : <span className="bg-[#D1FAE5] text-[#065F46] text-[10px] px-2 py-0.5 uppercase tracking-wider">ενεργό</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {!t.revoked && (
                      <button
                        onClick={async () => {
                          if (!window.confirm(`Ανάκληση token "${t.name}";`)) return;
                          try {
                            await api.delete(`/wp-sync/tokens/${t.id}`);
                            toast.success("Ανακλήθηκε");
                            await loadTokens();
                          } catch { toast.error("Αποτυχία"); }
                        }}
                        className="text-[#991B1B] hover:bg-[#FEE2E2] p-1"
                        title="Ανάκληση"
                      ><TrashIcon size={14} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Storage usage */}
      <StorageUsageSection form={form} set={set} />

      {/* Polylang Health Check + Sync Log */}
      <PolylangHealthSection />
      <WpSyncLogSection />

      {/* Sell-Your-Car public form integration */}
      <SellMyCarEmbedSection />

      {/* Full app backup */}
      <BackupSection />

      {/* Bulk photo re-optimization */}
      <PhotoOptimizeSection />

      {/* AI description audit */}
      <DescriptionAuditSection />
    </div>
  );
}

function DescriptionAuditSection() {
  const [report, setReport] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixing, setFixing] = useState(false);
  const [fixStatus, setFixStatus] = useState(null);
  const [deepStatus, setDeepStatus] = useState(null);
  const [deepStarting, setDeepStarting] = useState(false);
  const pollRef = useRef(null);
  const deepPollRef = useRef(null);
  const deepStartedRef = useRef(false);

  const pollStatus = useCallback(async () => {
    try {
      const r = await api.get("/admin/description-audit/fix-status");
      setFixStatus(r.data);
      if (r.data.running) {
        pollRef.current = setTimeout(pollStatus, 5000);
      }
    } catch {
      // never let a single failed request kill the polling loop
      pollRef.current = setTimeout(pollStatus, 10000);
    }
  }, []);

  const pollDeep = useCallback(async () => {
    try {
      const r = await api.get("/admin/description-audit/deep-status");
      setDeepStatus(r.data);
      if (r.data.running) {
        deepPollRef.current = setTimeout(pollDeep, 6000);
      } else if (r.data.finished_at && deepStartedRef.current) {
        deepStartedRef.current = false;
        setReport({ checked: r.data.total, flagged: r.data.flagged || [] });
        if ((r.data.flagged || []).length === 0) toast.success(`Βαθύς έλεγχος: ${r.data.total} περιγραφές — όλες καθαρές ✓`);
        else toast.warning(`Βαθύς έλεγχος: βρέθηκαν ${r.data.flagged.length} περιγραφές με αστήρικτες αναφορές`);
      }
    } catch {
      deepPollRef.current = setTimeout(pollDeep, 10000);
    }
  }, []);

  useEffect(() => {
    pollStatus();
    pollDeep();
    return () => {
      clearTimeout(pollRef.current);
      clearTimeout(deepPollRef.current);
    };
  }, [pollStatus, pollDeep]);

  const startDeep = async () => {
    setDeepStarting(true);
    try {
      const r = await api.post("/admin/description-audit/deep");
      deepStartedRef.current = true;
      setDeepStatus({ running: true, total: r.data.started, done: 0, current: "", flagged: [], errors: [] });
      toast.success(`Ξεκίνησε βαθύς έλεγχος AI σε ${r.data.started} περιγραφές (~10-15 δευτ. ανά αμάξι) — δείτε την πρόοδο.`, { duration: 8000 });
      deepPollRef.current = setTimeout(pollDeep, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setDeepStarting(false);
    }
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.get("/admin/description-audit");
      setReport(r.data);
      if (!r.data.flagged.length) toast.success(`Ελέγχθηκαν ${r.data.checked} περιγραφές — όλες καθαρές ✓`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία ελέγχου");
    } finally {
      setRunning(false);
    }
  };

  const fixAll = async () => {
    if (!report?.flagged?.length) return;
    setFixing(true);
    try {
      const r = await api.post("/admin/description-audit/fix", { car_ids: report.flagged.map((f) => f.car_id) });
      toast.success(`Ξεκίνησε η διόρθωση ${r.data.started} περιγραφών (~1 λεπτό ανά αμάξι) — δείτε την πρόοδο παρακάτω.`, { duration: 8000 });
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία");
    } finally {
      setFixing(false);
    }
  };

  return (
    <section className="bg-white border border-black/10 p-6" data-testid="description-audit-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="heading text-lg font-bold">Έλεγχος περιγραφών (AI)</h2>
          <p className="text-xs text-[#4B5563] mt-1">
            Γρήγορος έλεγχος: λέξεις-κλειδιά εξοπλισμού. Βαθύς έλεγχος AI: συγκρίνει ΚΑΘΕ αναφορά της περιγραφής με τον εξοπλισμό & τα τεχνικά στοιχεία.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={run}
            disabled={running}
            className="border border-black/20 hover:bg-black/5 disabled:opacity-50 px-4 py-2 text-sm font-medium"
            data-testid="run-description-audit"
          >
            {running ? "Έλεγχος..." : "Έλεγχος"}
          </button>
          <button
            onClick={startDeep}
            disabled={deepStarting || deepStatus?.running}
            className="border border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white disabled:opacity-50 px-4 py-2 text-sm font-medium"
            data-testid="deep-audit-btn"
          >
            {deepStatus?.running ? "Βαθύς έλεγχος..." : "Βαθύς έλεγχος AI"}
          </button>
          {report?.flagged?.length > 0 && (
            <button
              onClick={fixAll}
              disabled={fixing}
              className="bg-[#E60000] hover:bg-[#B80000] disabled:opacity-50 text-white px-4 py-2 text-sm font-medium"
              data-testid="fix-all-descriptions"
            >
              {fixing ? "Εκκίνηση..." : `Διόρθωση όλων (${report.flagged.length})`}
            </button>
          )}
        </div>
      </div>
      {deepStatus && (deepStatus.running || deepStatus.interrupted || (deepStatus.finished_at && deepStatus.total > 0)) && (
        <div className="mb-4 border border-[#0055FF]/30 bg-[#EFF6FF] p-3" data-testid="deep-audit-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-medium">
              {deepStatus.running
                ? `Βαθύς έλεγχος AI: ${deepStatus.done}/${deepStatus.total}${deepStatus.current ? ` — τώρα: ${deepStatus.current}` : ""}`
                : deepStatus.interrupted
                  ? `⚠ Ο βαθύς έλεγχος διακόπηκε στο ${deepStatus.done}/${deepStatus.total} — πατήστε ξανά «Βαθύς έλεγχος AI»`
                  : `Βαθύς έλεγχος ολοκληρώθηκε: ${deepStatus.done}/${deepStatus.total} περιγραφές`}
              {(deepStatus.flagged || []).length > 0 ? ` · ευρήματα: ${deepStatus.flagged.length}` : deepStatus.running || deepStatus.interrupted ? "" : " · καμία παράβαση"}
            </span>
            {deepStatus.running
              ? <span className="animate-pulse text-[#0055FF]">●</span>
              : (deepStatus.flagged || []).length > 0 && (
                  <button
                    onClick={() => setReport({ checked: deepStatus.total, flagged: deepStatus.flagged || [] })}
                    className="underline text-[#0055FF] text-xs"
                    data-testid="show-deep-findings"
                  >
                    Εμφάνιση ευρημάτων
                  </button>
                )}
          </div>
          <div className="h-2 bg-black/10">
            <div className={`h-2 ${deepStatus.running ? "bg-[#0055FF]" : "bg-[#0A7F3F]"} transition-all`} style={{ width: `${deepStatus.total ? Math.round((deepStatus.done / deepStatus.total) * 100) : 0}%` }} />
          </div>
          {deepStatus.errors?.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {deepStatus.errors.slice(0, 10).map((er, i) => (
                <div key={i} className="text-[11px] text-[#E60000]">⚠ {er}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {fixStatus && (fixStatus.running || fixStatus.interrupted || (fixStatus.finished_at && fixStatus.total > 0)) && (
        <div className="mb-4 border border-black/10 bg-[#F9FAFB] p-3" data-testid="audit-fix-progress">
          <div className="flex items-center justify-between text-xs mb-2">
            <span className="font-medium">
              {fixStatus.running
                ? `Διόρθωση σε εξέλιξη: ${fixStatus.done}/${fixStatus.total}${fixStatus.current ? ` — τώρα: ${fixStatus.current}` : ""}`
                : fixStatus.interrupted
                  ? `⚠ Η διόρθωση διακόπηκε στο ${fixStatus.done}/${fixStatus.total} (πιθανό restart/deploy) — πατήστε ξανά «Διόρθωση όλων» για τα υπόλοιπα`
                  : `Η διόρθωση ολοκληρώθηκε: ${fixStatus.done}/${fixStatus.total} — πατήστε «Έλεγχος» για επιβεβαίωση`}
            </span>
            {fixStatus.running && <span className="animate-pulse text-[#0055FF]">●</span>}
          </div>
          <div className="h-2 bg-black/10">
            <div
              className={`h-2 ${fixStatus.running ? "bg-[#0055FF]" : fixStatus.interrupted ? "bg-[#F59E0B]" : "bg-[#0A7F3F]"} transition-all`}
              style={{ width: `${fixStatus.total ? Math.round((fixStatus.done / fixStatus.total) * 100) : 0}%` }}
            />
          </div>
          {fixStatus.errors?.length > 0 && (
            <div className="mt-2 space-y-0.5">
              {fixStatus.errors.map((er, i) => (
                <div key={i} className="text-[11px] text-[#E60000]">⚠ {er}</div>
              ))}
            </div>
          )}
        </div>
      )}
      {report && (
        <div className="space-y-2" data-testid="description-audit-results">
          <div className="text-xs text-[#4B5563]">
            Ελέγχθηκαν <strong>{report.checked}</strong> περιγραφές · Βρέθηκαν <strong className={report.flagged.length ? "text-[#E60000]" : "text-[#0A7F3F]"}>{report.flagged.length}</strong> με πιθανό ανύπαρκτο εξοπλισμό
          </div>
          {report.flagged.map((f) => (
            <div key={f.car_id} className="border border-[#E60000]/40 bg-[#FEF2F2] px-3 py-2 text-xs flex items-center justify-between gap-3" data-testid={`audit-flag-${f.car_id}`}>
              <div>
                <strong>{f.title}</strong>{f.status === "sold" ? " (πουλημένο)" : ""} — αναφέρει: <span className="font-mono">{f.violations.join(", ")}</span>
              </div>
              <a href={`/cars/${f.car_id}`} className="shrink-0 underline text-[#0055FF]">Άνοιγμα</a>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function StorageUsageSection({ form, set }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/storage/usage");
      setUsage(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Αποτυχία υπολογισμού χώρου");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pct = usage?.percent_used ?? 0;
  const barColor = pct >= 90 ? "bg-[#DC2626]" : pct >= 70 ? "bg-[#D97706]" : "bg-[#0A7F3F]";

  return (
    <section className="bg-white border border-black/10 p-6" data-testid="storage-usage-section">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563]">ΧΩΡΟΣ</div>
          <h3 className="heading text-lg font-bold">Χώρος Αποθήκευσης</h3>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="border border-black/20 hover:bg-black/5 disabled:opacity-50 px-4 py-2 text-sm font-medium"
          data-testid="storage-usage-refresh-btn"
        >
          {loading ? "Υπολογισμός..." : "Ανανέωση"}
        </button>
      </div>

      {err && <div className="text-sm text-[#DC2626] mb-3" data-testid="storage-usage-error">{err}</div>}

      {usage && (
        <div className="space-y-4">
          <div>
            <div className="flex items-baseline justify-between mb-1.5">
              <span className="text-sm font-semibold" data-testid="storage-usage-total">
                {usage.total_mb} MB <span className="font-normal text-[#4B5563]">από {usage.quota_mb} MB</span>
              </span>
              <span className="text-sm text-[#4B5563]" data-testid="storage-usage-remaining">
                Ελεύθερα: <strong className="text-[#0A0A0A]">{usage.remaining_mb} MB</strong> ({pct}% σε χρήση)
              </span>
            </div>
            <div className="h-3 bg-[#E5E7EB] overflow-hidden">
              <div className={`h-full ${barColor} transition-all`} style={{ width: `${Math.min(pct, 100)}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="border border-black/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5563]">Φωτογραφίες</div>
              <div className="font-semibold" data-testid="storage-usage-photos">{usage.photos_mb} MB</div>
              <div className="text-xs text-[#4B5563]">{usage.photos_count} αρχεία στο cloud</div>
            </div>
            <div className="border border-black/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5563]">Βάση δεδομένων</div>
              <div className="font-semibold" data-testid="storage-usage-db">{usage.db_mb} MB</div>
              <div className="text-xs text-[#4B5563]">αυτοκίνητα, ρυθμίσεις, logs</div>
            </div>
            <div className="border border-black/10 p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[#4B5563]">Όριο χώρου (MB)</div>
              <input
                type="number"
                min="100"
                value={form.storage_quota_mb ?? 5120}
                onChange={(e) => set("storage_quota_mb", parseInt(e.target.value || "0", 10))}
                className="w-full border border-black/20 px-2 py-1 text-sm font-semibold focus:outline-none focus:border-black"
                data-testid="storage-quota-input"
              />
              <div className="text-xs text-[#4B5563] mt-1">Αποθηκεύεται με το κουμπί «Αποθήκευση»</div>
            </div>
          </div>

          {usage.local_mb > 0 && (
            <div className="text-xs text-[#4B5563]">
              + {usage.local_mb} MB προσωρινά τοπικά αρχεία (δεν προσμετρώνται — καθαρίζονται σε κάθε deploy)
            </div>
          )}
          {usage.storage_error && (
            <div className="text-xs text-[#DC2626]">Σφάλμα cloud storage: {usage.storage_error}</div>
          )}
        </div>
      )}
    </section>
  );
}

function PolylangHealthSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const run = useCallback(async () => {
    setLoading(true);
    setErr("");
    setData(null);
    try {
      const r = await api.get("/wp-sync/polylang-health");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Αποτυχία επικοινωνίας με WordPress.");
    } finally {
      setLoading(false);
    }
  }, []);

  const summary = data?.summary || {};
  const anyOrphan = (summary.orphan_el || 0) + (summary.orphan_en || 0) + (summary.no_lang || 0) > 0;

  return (
    <section className="bg-white border border-black/10 p-6" data-testid="polylang-health-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="heading text-lg font-bold">Polylang Health Check</h3>
          <p className="text-sm text-[#4B5563]">
            Έλεγχος αν όλες οι κατηγορίες, τα attributes και τα προϊόντα έχουν σωστά συνδεδεμένη EL↔EN μετάφραση.
          </p>
        </div>
        <button
          onClick={run}
          disabled={loading}
          data-testid="polylang-health-run"
          className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-4 py-2 text-sm"
        >
          {loading ? "Έλεγχος..." : "Έλεγχος τώρα"}
        </button>
      </div>

      {err && (
        <div className="bg-[#FEE2E2] border-l-4 border-[#DC2626] p-3 text-sm text-[#7F1D1D] mb-3" data-testid="polylang-health-error">
          {err}
        </div>
      )}

      {data && !data.ok && (
        <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-3 text-sm text-[#78350F] mb-3">
          {data.error || "Το Polylang δεν είναι διαθέσιμο σε αυτό το WP."}
        </div>
      )}

      {data && data.ok && (
        <div className="space-y-3" data-testid="polylang-health-result">
          <div className={`p-3 border-l-4 text-sm ${anyOrphan ? "bg-[#FEF3C7] border-[#F59E0B] text-[#78350F]" : "bg-[#F0FDF4] border-[#10B981] text-[#065F46]"}`}>
            <div className="font-bold mb-1">
              {anyOrphan ? "⚠ Βρέθηκαν orphan όροι" : "✓ Όλα συνδεδεμένα"}
            </div>
            <div className="grid grid-cols-4 gap-2 font-mono text-xs">
              <div>Linked: <b>{summary.linked || 0}</b></div>
              <div>Orphan EL: <b>{summary.orphan_el || 0}</b></div>
              <div>Orphan EN: <b>{summary.orphan_en || 0}</b></div>
              <div>Χωρίς γλώσσα: <b>{summary.no_lang || 0}</b></div>
            </div>
          </div>

          <table className="w-full text-xs">
            <thead className="bg-[#F9FAFB] border border-black/10">
              <tr>
                <th className="text-left px-2 py-1.5">Ταξινομία</th>
                <th className="text-right px-2 py-1.5">Σύνολο</th>
                <th className="text-right px-2 py-1.5">EL</th>
                <th className="text-right px-2 py-1.5">EN</th>
                <th className="text-right px-2 py-1.5">Linked</th>
                <th className="text-right px-2 py-1.5 text-[#B91C1C]">Orphan EL</th>
                <th className="text-right px-2 py-1.5 text-[#B91C1C]">Orphan EN</th>
                <th className="text-right px-2 py-1.5 text-[#6B7280]">No lang</th>
              </tr>
            </thead>
            <tbody>
              {(data.taxonomies || []).map((t) => (
                <tr key={t.taxonomy} className="border-b border-black/5">
                  <td className="px-2 py-1.5 font-medium">
                    {t.label}
                    {!t.exists && <span className="ml-1 text-[10px] text-[#B91C1C]">(δεν υπάρχει)</span>}
                    {(t.orphan_el_names?.length > 0 || t.orphan_en_names?.length > 0) && (
                      <div className="text-[10px] text-[#B91C1C] mt-0.5 font-normal">
                        {t.orphan_el_names?.length > 0 && <div>EL orphans: {t.orphan_el_names.join(", ")}</div>}
                        {t.orphan_en_names?.length > 0 && <div>EN orphans: {t.orphan_en_names.join(", ")}</div>}
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-right font-mono">{t.total}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{t.el}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{t.en}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[#065F46]">{t.linked}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${t.orphan_el > 0 ? "text-[#B91C1C] font-bold" : ""}`}>{t.orphan_el}</td>
                  <td className={`px-2 py-1.5 text-right font-mono ${t.orphan_en > 0 ? "text-[#B91C1C] font-bold" : ""}`}>{t.orphan_en}</td>
                  <td className="px-2 py-1.5 text-right font-mono text-[#6B7280]">{t.no_lang}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.products && (
            <div className="mt-3 border border-black/10 p-3 text-xs" data-testid="polylang-products-summary">
              <div className="font-bold text-sm mb-2">Προϊόντα BESIKOS</div>
              <div className="grid grid-cols-6 gap-2 font-mono">
                <div>Σύνολο: <b>{data.products.total}</b></div>
                <div>EL: <b>{data.products.el}</b></div>
                <div>EN: <b>{data.products.en}</b></div>
                <div className="text-[#065F46]">Linked: <b>{data.products.linked}</b></div>
                <div className={data.products.orphan_el > 0 ? "text-[#B91C1C]" : ""}>Orphan EL: <b>{data.products.orphan_el}</b></div>
                <div className={data.products.orphan_en > 0 ? "text-[#B91C1C]" : ""}>Orphan EN: <b>{data.products.orphan_en}</b></div>
              </div>
              {(data.products.orphan_el_titles?.length > 0 || data.products.orphan_en_titles?.length > 0) && (
                <div className="mt-2 text-[10px] text-[#B91C1C]">
                  {data.products.orphan_el_titles?.length > 0 && <div>EL χωρίς EN: {data.products.orphan_el_titles.join(", ")}</div>}
                  {data.products.orphan_en_titles?.length > 0 && <div>EN χωρίς EL: {data.products.orphan_en_titles.join(", ")}</div>}
                </div>
              )}
            </div>
          )}

          <div className="text-[10px] text-[#6B7280] font-mono">
            Plugin v{data.plugin_ver} · έλεγχος: {data.checked_at}
          </div>
        </div>
      )}
    </section>
  );
}


function WpSyncLogSection() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const r = await api.get("/wp-sync/log?limit=100");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Αποτυχία φόρτωσης log.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const statusColor = (s) => {
    if (s === "ok") return "bg-[#D1FAE5] text-[#065F46]";
    if (s === "error") return "bg-[#FEE2E2] text-[#7F1D1D]";
    if (s === "warn") return "bg-[#FEF3C7] text-[#78350F]";
    if (s === "skipped") return "bg-[#F3F4F6] text-[#4B5563]";
    return "bg-[#DBEAFE] text-[#1E3A8A]";
  };

  return (
    <section className="bg-white border border-black/10 p-6" data-testid="wp-sync-log-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="heading text-lg font-bold">WP Sync Log</h3>
          <p className="text-sm text-[#4B5563]">
            Ιστορικό των τελευταίων συγχρονισμών με το WordPress — τι στάλθηκε από εδώ (backend triggers) και τι έγινε στην πλευρά του WP (upsert / delete / errors).
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          data-testid="wp-sync-log-refresh"
          className="bg-[#374151] hover:bg-[#1F2937] disabled:opacity-50 text-white px-4 py-2 text-sm"
        >
          {loading ? "Φόρτωση..." : "Ανανέωση"}
        </button>
      </div>

      {err && (
        <div className="bg-[#FEE2E2] border-l-4 border-[#DC2626] p-3 text-sm text-[#7F1D1D] mb-3">{err}</div>
      )}

      {data && (
        <div className="space-y-4">
          {/* Remote (WP) log */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-sm">
                Ενέργειες στο WordPress ({data.remote?.count || 0})
              </h4>
              {data.remote?.last_sync && (
                <span className="text-xs text-[#4B5563] font-mono">Τελευταίο sync: {data.remote.last_sync}</span>
              )}
            </div>
            {!data.remote?.ok && (
              <div className="bg-[#FEF3C7] border-l-4 border-[#F59E0B] p-2 text-xs text-[#78350F] mb-2">
                {data.remote?.error || "Δεν λήφθηκε remote log."}
              </div>
            )}
            {data.remote?.events?.length ? (
              <div className="max-h-72 overflow-y-auto border border-black/10">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Ώρα</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">Action</th>
                      <th className="text-left px-2 py-1.5">Vehicle</th>
                      <th className="text-left px-2 py-1.5">Μήνυμα</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.remote.events.map((e, i) => (
                      <tr key={`${e.ts}-${i}`} className="border-b border-black/5">
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{e.ts}</td>
                        <td className="px-2 py-1">
                          <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusColor(e.status)}`}>
                            {e.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 font-mono">{e.action}</td>
                        <td className="px-2 py-1 font-mono">{e.vehicle_uid || "—"}</td>
                        <td className="px-2 py-1">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              data.remote?.ok && <div className="text-xs text-[#6B7280]">Δεν υπάρχουν καταγραφές ακόμη.</div>
            )}
          </div>

          {/* Local (backend triggers) */}
          <div>
            <h4 className="font-bold text-sm mb-2">Backend triggers ({data.local?.count || 0})</h4>
            {data.local?.events?.length ? (
              <div className="max-h-60 overflow-y-auto border border-black/10">
                <table className="w-full text-xs">
                  <thead className="bg-[#F9FAFB] sticky top-0">
                    <tr>
                      <th className="text-left px-2 py-1.5">Ώρα (UTC)</th>
                      <th className="text-left px-2 py-1.5">Status</th>
                      <th className="text-left px-2 py-1.5">Action</th>
                      <th className="text-left px-2 py-1.5">Vehicle</th>
                      <th className="text-left px-2 py-1.5">HTTP</th>
                      <th className="text-left px-2 py-1.5">ms</th>
                      <th className="text-left px-2 py-1.5">Μήνυμα</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.local.events.map((e) => (
                      <tr key={e.id} className="border-b border-black/5">
                        <td className="px-2 py-1 font-mono whitespace-nowrap">{e.triggered_at?.slice(0, 19).replace("T", " ")}</td>
                        <td className="px-2 py-1">
                          <span className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase ${statusColor(e.status)}`}>
                            {e.status}
                          </span>
                        </td>
                        <td className="px-2 py-1 font-mono">{e.action}</td>
                        <td className="px-2 py-1 font-mono">{e.vehicle_uid || "—"}</td>
                        <td className="px-2 py-1 font-mono">{e.http_status || "—"}</td>
                        <td className="px-2 py-1 font-mono">{e.duration_ms ?? "—"}</td>
                        <td className="px-2 py-1">{e.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-xs text-[#6B7280]">Δεν έχει σταλεί ακόμη κανένα webhook.</div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


function PhotoOptimizeSection() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const run = async () => {
    if (!window.confirm("Επανα-βελτιστοποίηση όλων των φωτογραφιών; Μπορεί να διαρκέσει 2-5 λεπτά. Συνεχίζουμε;")) return;
    setRunning(true);
    setResult(null);
    try {
      const r = await api.post("/admin/photos/bulk-reoptimize");
      setResult(r.data);
      toast.success(`Ολοκληρώθηκε · ${r.data.processed} φωτο βελτιστοποιήθηκαν`);
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Μόνο admin" : "Αποτυχία");
    } finally {
      setRunning(false);
    }
  };
  const fmtMB = (b) => (b / (1024 * 1024)).toFixed(1) + " MB";
  return (
    <section className="bg-white border border-black/10 p-6" data-testid="photo-optimize-section">
      <h3 className="heading text-lg font-bold mb-1">Bulk Βελτιστοποίηση Φωτογραφιών</h3>
      <p className="text-sm text-[#4B5563] mb-4">
        Επανα-βελτιστοποίηση όλων των υπαρχόντων φωτογραφιών (resize σε 1600px + WebP Q82).
        Οι νέες φωτογραφίες βελτιστοποιούνται ήδη αυτόματα — αυτό είναι για φωτο που υπήρχαν
        πριν την ενεργοποίηση του feature. Τρέξτε μία φορά.
      </p>
      <button
        type="button"
        onClick={run}
        disabled={running}
        data-testid="bulk-optimize-btn"
        className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-5 py-3 text-sm flex items-center gap-2"
      >
        {running ? "Σε εξέλιξη (μην κλείσετε τη σελίδα)..." : "Έναρξη βελτιστοποίησης"}
      </button>
      {result && (
        <div className="mt-4 bg-[#F0FDF4] border border-[#10B981] p-4 text-sm" data-testid="optimize-result">
          <div className="font-bold text-[#065F46] mb-2">Αποτελέσματα</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs font-mono">
            <div><div className="text-[#4B5563]">Επεξεργάστηκαν</div><div className="text-lg font-bold">{result.processed}</div></div>
            <div><div className="text-[#4B5563]">Παραλείφθηκαν</div><div className="text-lg font-bold">{result.skipped}</div></div>
            <div><div className="text-[#4B5563]">Αποτυχίες</div><div className="text-lg font-bold">{result.failed}</div></div>
            <div><div className="text-[#4B5563]">Εξοικονόμηση</div><div className="text-lg font-bold text-[#065F46]">{fmtMB(result.saved_bytes)} ({result.saved_pct}%)</div></div>
          </div>
        </div>
      )}
    </section>
  );
}

function BackupSection() {
  const [downloading, setDownloading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const fileRef = useRef(null);
  const download = async () => {
    setDownloading(true);
    try {
      const r = await api.get("/admin/backup.zip", { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      link.href = url;
      link.download = `besikos-backup-${ts}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Το backup κατέβηκε");
    } catch (e) {
      toast.error(e?.response?.status === 403 ? "Μόνο admin" : "Αποτυχία λήψης backup");
    } finally {
      setDownloading(false);
    }
  };
  const restore = async (file) => {
    if (!file) return;
    if (confirmText !== "RESTORE") {
      toast.error('Πληκτρολογήστε ακριβώς "RESTORE" για επιβεβαίωση');
      return;
    }
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const colls = r.data?.collections || {};
      const photos = r.data?.photos_restored || 0;
      const errs = (r.data?.errors || []).length;
      toast.success(`Restore ολοκληρώθηκε · ${Object.keys(colls).length} collections, ${photos} φωτο${errs ? ` · ${errs} σφάλματα` : ""}`);
      setConfirmText("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Αποτυχία restore");
    } finally {
      setRestoring(false);
    }
  };
  return (
    <section className="bg-white border border-black/10 p-6" data-testid="backup-section">
      <h3 className="heading text-lg font-bold mb-1 flex items-center gap-2">
        <DownloadSimpleIcon size={20} /> Backup & Restore
      </h3>
      <p className="text-sm text-[#4B5563] mb-4">
        Λήψη πλήρους αντιγράφου ασφαλείας σε .zip — περιέχει όλα τα αυτοκίνητα, χρήστες, ρυθμίσεις,
        μάρκες, εξοπλισμό, αιτήσεις πώλησης και όλες τις φωτογραφίες. Συνιστάται 1 φορά την εβδομάδα.
      </p>
      <button
        type="button"
        onClick={download}
        disabled={downloading}
        data-testid="download-backup-btn"
        className="bg-[#0A0A0A] hover:bg-[#1F2937] disabled:opacity-50 text-white px-5 py-3 text-sm flex items-center gap-2"
      >
        <DownloadSimpleIcon size={16} />
        {downloading ? "Δημιουργία backup..." : "Λήψη Backup (.zip)"}
      </button>
      <p className="text-xs text-[#4B5563] mt-3 font-mono">
        Μόνο admin · Αποθηκεύστε το zip σε εξωτερικό cloud (Google Drive / Dropbox) για ασφάλεια.
      </p>

      <div className="mt-8 pt-6 border-t border-black/10">
        <h4 className="heading text-base font-bold mb-1 text-[#991B1B]">⚠ Επαναφορά (Restore)</h4>
        <p className="text-sm text-[#4B5563] mb-3">
          Επιλέξτε ένα προηγούμενο <span className="font-mono">.zip</span> backup. Η ενέργεια θα <b>διαγράψει
          όλα τα τρέχοντα δεδομένα</b> (αυτοκίνητα, χρήστες, ρυθμίσεις, φωτογραφίες) και θα τα
          αντικαταστήσει με τα δεδομένα του backup. Δεν αναιρείται.
        </p>
        <div className="bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] text-xs p-3 mb-3">
          Πληκτρολογήστε <span className="font-mono font-bold">RESTORE</span> για ξεκλείδωμα του κουμπιού.
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
            placeholder='Πληκτρολογήστε "RESTORE"'
            className="px-3 py-2 border border-black/20 text-sm font-mono w-48"
            data-testid="restore-confirm-input"
          />
          <input
            ref={fileRef}
            type="file"
            accept=".zip,application/zip"
            disabled={confirmText !== "RESTORE" || restoring}
            onChange={(e) => restore(e.target.files?.[0])}
            data-testid="restore-file-input"
            className="text-xs flex-1"
          />
          {restoring && <span className="text-xs text-[#4B5563] font-mono">Restore σε εξέλιξη...</span>}
        </div>
      </div>
    </section>
  );
}

function SellMyCarEmbedSection() {
  const publicUrl = `${window.location.origin}/sell-my-car`;
  const iframeSnippet = `<iframe src="${publicUrl}" style="width:100%;min-height:1400px;border:0" loading="lazy" title="Πούλησέ μας το αυτοκίνητό σου"></iframe>`;
  const linkSnippet = `<a href="${publicUrl}" target="_blank" rel="noopener" class="button">Πούλησέ μας το αυτοκίνητό σου</a>`;
  const copy = (text, label) => {
    navigator.clipboard.writeText(text).then(() => toast.success(`Αντιγράφηκε: ${label}`));
  };
  return (
    <section className="bg-white border border-black/10 p-6" data-testid="sell-form-embed-section">
      <h3 className="heading text-lg font-bold mb-1 flex items-center gap-2">
        Δημόσια Φόρμα Πώλησης
      </h3>
      <p className="text-sm text-[#4B5563] mb-4">
        Οι αιτήσεις που έρχονται από αυτή τη φόρμα εμφανίζονται στο tab «Αιτήσεις Πώλησης».
        Embed στο WordPress site με ένα από τα παρακάτω snippets.
      </p>
      <div className="space-y-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-1.5">Δημόσιο URL</div>
          <div className="flex gap-2">
            <input
              readOnly
              value={publicUrl}
              className="flex-1 px-3 py-2 border border-black/20 bg-[#F9FAFB] text-sm font-mono"
              data-testid="sell-form-url"
              onFocus={(e) => e.target.select()}
            />
            <button
              type="button"
              onClick={() => copy(publicUrl, "URL")}
              className="border border-black/15 hover:bg-black/5 px-3"
              title="Αντιγραφή"
            >
              <CopyIcon size={16} />
            </button>
            <a
              href={publicUrl}
              target="_blank"
              rel="noreferrer"
              className="border border-black/15 hover:bg-black/5 px-3 py-2 text-xs uppercase tracking-wider"
            >
              Προεπισκόπηση
            </a>
          </div>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-1.5">Επιλογή 1 — Iframe Embed (στο WordPress μένει ο χρήστης στο site σας)</div>
          <textarea
            readOnly
            rows={3}
            value={iframeSnippet}
            className="w-full px-3 py-2 border border-black/20 bg-[#F9FAFB] text-xs font-mono"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={() => copy(iframeSnippet, "iframe snippet")}
            className="mt-1 text-xs uppercase tracking-wider border border-black/15 hover:bg-black/5 px-3 py-1.5 flex items-center gap-1.5"
          >
            <CopyIcon size={12} /> Αντιγραφή iframe
          </button>
        </div>

        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#4B5563] mb-1.5">Επιλογή 2 — Απλό κουμπί / link (ανοίγει σε νέα καρτέλα)</div>
          <textarea
            readOnly
            rows={2}
            value={linkSnippet}
            className="w-full px-3 py-2 border border-black/20 bg-[#F9FAFB] text-xs font-mono"
            onFocus={(e) => e.target.select()}
          />
          <button
            type="button"
            onClick={() => copy(linkSnippet, "link snippet")}
            className="mt-1 text-xs uppercase tracking-wider border border-black/15 hover:bg-black/5 px-3 py-1.5 flex items-center gap-1.5"
          >
            <CopyIcon size={12} /> Αντιγραφή link
          </button>
        </div>

        <div className="bg-[#FEF3C7] border border-[#FCD34D] text-[#92400E] p-3 text-xs">
          <b>Email ειδοποιήσεις:</b> για να λαμβάνετε email σε κάθε νέα αίτηση, ζητήστε από τον admin να ορίσει
          <span className="font-mono mx-1">RESEND_API_KEY</span> και <span className="font-mono">DEALER_NOTIFY_EMAIL</span>
          στο backend .env (χρειάζεται δωρεάν λογαριασμός Resend). Αν δεν έχουν οριστεί,
          τα leads αποθηκεύονται κανονικά εδώ — απλά δεν στέλνεται email.
        </div>
      </div>
    </section>
  );
}
